import { useState, FormEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, BarChart3 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { logger } from '../utils/logger';
import { cn } from '../utils/cn';
import { LoadingIndicator } from '../components/feedback/LoadingIndicator';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { login } = useAuthStore();

  const isEmailValid = useMemo(() => EMAIL_REGEX.test(email), [email]);
  const isPasswordValid = password.length > 0;
  const isFormValid = isEmailValid && isPasswordValid;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isFormValid) return;

    setIsLoading(true);

    try {
      logger.info('Attempting login', { email });
      const response = await authService.login(email, password);

      if (response.success && response.data?.accessToken && response.data?.refreshToken) {
        login({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        });
        logger.info('Login successful, redirecting to chat');
        navigate('/');
      } else {
        setError(response.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      const apiError = err as Error & { response?: { data?: { message?: string } } };
      logger.error('Login error', apiError);
      setError(apiError.response?.data?.message || apiError.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4">
      <div className={cn('w-full max-w-md rounded-xl shadow-lg bg-white p-8')}>
        {/* Logo, heading, subheading */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary-600 text-white shadow-md">
            <BarChart3 className="h-8 w-8" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900">Welcome to Oquis</h1>
          <p className="mt-1 text-sm text-neutral-500 text-center">
            Sign in to access your business intelligence
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div
            role="alert"
            className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600 mt-0.5" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className={cn(
                'w-full rounded-lg border px-3 py-2.5 text-sm text-neutral-900 placeholder-neutral-500',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors'
              )}
              disabled={isLoading}
              autoComplete="email"
              tabIndex={0}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={cn(
                'w-full rounded-lg border px-3 py-2.5 text-sm text-neutral-900 placeholder-neutral-500',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors'
              )}
              disabled={isLoading}
              autoComplete="current-password"
              tabIndex={0}
            />
          </div>

          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            tabIndex={0}
            className={cn(
              'w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white',
              'bg-primary-600 hover:bg-primary-700',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            {isLoading ? (
              <>
                <LoadingIndicator variant="button" size="sm" />
                <span>Signing in…</span>
              </>
            ) : (
              <span>Sign In</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
